import type { ReactNode } from "react";
import type { CommunityAdminProjectApproval, CommunityAdminProjectRemoval } from "@squeek/module-contracts/community-admin-v1";
import { Chip, type ChipTone } from "@squeek/ui";
import { errorMessage } from "./api";

/** Loading, error, or the content once data is present. */
export function QueryState<T>({ query, children }: { query: { data: T | undefined; error: unknown; isPending: boolean }; children: (data: T) => ReactNode }) {
  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.error) return <ErrorNotice error={query.error} />;
  return <p className="py-6 text-sm text-[var(--c-fg-3)]">Loading…</p>;
}
export function ErrorNotice({ error }: { error: unknown }) {
  return <p className="py-2 text-sm text-[var(--c-bad)]" role="alert">{errorMessage(error)}</p>;
}
export interface ReviewableProject {
  readonly id: string;
  readonly name: string;
  readonly origin: "imd" | "website" | "independent";
  readonly status: "pending" | "verified" | "failed";
  readonly removal: CommunityAdminProjectRemoval | null;
  readonly approval: CommunityAdminProjectApproval | null;
}
type ProjectReviewState = "removed" | "pending" | "failed" | "approved" | "awaiting_review";
/** Removal outranks approval; pending IMD and verified websites await the team. */
export function projectReviewState({ origin, status, removal, approval }: Omit<ReviewableProject, "id" | "name">): ProjectReviewState {
  if (removal !== null) return "removed";
  if (approval !== null) return "approved";
  if (status === "verified" || (origin === "imd" && status === "pending")) return "awaiting_review";
  return status;
}
const REVIEW_STATE_CHIPS: Record<ProjectReviewState, { label: string; tone: ChipTone }> = {
  removed: { label: "Removed", tone: "failing" }, pending: { label: "Pending", tone: "neutral" },
  failed: { label: "Failed", tone: "stalled" }, approved: { label: "Approved", tone: "active" },
  awaiting_review: { label: "Awaiting review", tone: "info" },
};
export function ProjectStatusChip(props: Omit<ReviewableProject, "id" | "name">) {
  const chip = REVIEW_STATE_CHIPS[projectReviewState(props)];
  return <Chip tone={chip.tone}>{chip.label}</Chip>;
}
